import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Archive, Upload, Check, Loader2, FileText, Download, Trash2, Shield, Crown } from 'lucide-react';
import { useAuth, formatApiError } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  'Property Legal', 'Appraisal Report', 'Property Photo', 'Investor KYC',
  'Marriage Certificate', 'Birth Certificate', 'Title Deed', 'Insurance',
  'Lien Sweep', 'Inspection Report', 'Other'
];

const TRIGGER_CATEGORIES = ['Marriage Certificate', 'Birth Certificate'];

export default function ImperialArchives() {
  const { user, loading, authHeaders } = useAuth();
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [lineage, setLineage] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showUpload, setShowUpload] = useState(false);
  const [form, setForm] = useState({
    title: '', category: 'Property Legal', description: '',
    property_id: '', file: null, fileB64: null,
  });
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(null);

  const loadAll = async () => {
    try {
      const [d, s, l] = await Promise.all([
        axios.get(`${API}/archives`, { headers: authHeaders(), withCredentials: true }),
        axios.get(`${API}/archives/stats`, { headers: authHeaders(), withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/lineage/status`),
      ]);
      setDocs(d.data); setStats(s.data); setLineage(l.data);
    } catch (e) { console.error("Silent failure:", e); }
  };

  useEffect(() => { if (user) loadAll(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <div className="px-6 md:px-24 py-32 text-center text-[#A3A3A3]">Verifying seal…</div>;
  if (!user) return <Navigate to="/login" replace />;

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 12 * 1024 * 1024) { toast.error('Max 12MB per document.'); return; }
    const reader = new FileReader();
    reader.onload = () => {
      const b64 = String(reader.result).split(',')[1];
      setForm((p) => ({ ...p, file: f, fileB64: b64 }));
    };
    reader.readAsDataURL(f);
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!form.fileB64) { toast.error('Attach a file first.'); return; }
    setUploading(true);
    try {
      await axios.post(`${API}/archives/upload`, {
        title: form.title,
        category: form.category,
        filename: form.file.name,
        mime_type: form.file.type || 'application/octet-stream',
        content_b64: form.fileB64,
        property_id: form.property_id || null,
        description: form.description || null,
      }, { headers: authHeaders(), withCredentials: true });
      toast.success('Document sealed in archives.');
      setShowUpload(false);
      setForm({ title: '', category: 'Property Legal', description: '', property_id: '', file: null, fileB64: null });
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Upload refused.');
    } finally { setUploading(false); }
  };

  const verify = async (id) => {
    setBusy(id);
    try {
      const { data } = await axios.post(`${API}/archives/${id}/verify`, {}, { headers: authHeaders(), withCredentials: true });
      if (data.lineage_trigger_fired) {
        toast.success(`🔱 ${data.lineage_state.tier === 3 ? 'ANCESTRAL LAW' : 'LINEAGE LAW'} ACTIVATED — Doctrine now ${data.current_doctrine}`);
      } else {
        toast.success('Document verified under admiral seal.');
      }
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Verification failed.');
    }
    setBusy(null);
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this document from the archives?')) return;
    setBusy(id);
    try {
      await axios.delete(`${API}/archives/${id}`, { headers: authHeaders(), withCredentials: true });
      toast.success('Document removed.');
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || 'Removal refused.');
    }
    setBusy(null);
  };

  const download = async (id) => {
    try {
      const { data } = await axios.get(`${API}/archives/${id}/download`, { headers: authHeaders(), withCredentials: true });
      const link = document.createElement('a');
      link.href = `data:${data.mime_type};base64,${data.content_b64}`;
      link.download = data.filename;
      link.click();
    } catch { toast.error('Download refused.'); }
  };

  const filtered = filter === 'all' ? docs : docs.filter((d) => d.category === filter);
  const isAdmin = user.role === 'admin';

  return (
    <div className="px-6 md:px-12 lg:px-24 py-16 min-h-[80vh]">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6 flex items-center gap-3">
            <Archive size={14} strokeWidth={1.5} /> Imperial Archives
          </div>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extralight leading-[1] tracking-tight">
            Armored<br />
            <span className="font-display italic text-[#D4AF37]">document vault.</span>
          </h1>
        </div>
        <div className="md:col-span-5 flex items-end">
          <p className="text-base text-[#A3A3A3] leading-relaxed font-light">
            SHA-256 fingerprinted. Role-gated. Documents linked to properties and investors.
            Verified Marriage and Birth certificates ascend the Lineage Law.
          </p>
        </div>
      </div>

      {/* Lineage Status Indicator */}
      {lineage && <LineageIndicator lineage={lineage} />}

      {/* Stats (admin) */}
      {isAdmin && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-b border-[#D4AF37]/15 py-8 my-12">
          <Stat label="Documents" value={stats.total_documents} />
          <Stat label="Verified" value={stats.verified_documents} accent />
          <Stat label="Storage" value={`${stats.total_storage_mb} MB`} />
          <Stat label="Categories Used" value={Object.values(stats.by_category).filter((c) => c > 0).length} />
        </div>
      )}

      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div className="flex gap-6 overflow-x-auto">
          {['all', ...CATEGORIES].map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`archives-filter-${c.replace(/\s+/g, '-').toLowerCase()}`}
              className={`text-[10px] tracking-[0.3em] uppercase whitespace-nowrap transition-colors ${
                filter === c ? 'text-[#D4AF37]' : 'text-[#A3A3A3] hover:text-[#F5F5F5]'
              }`}
            >
              {c === 'all' ? `All (${docs.length})` : c}
            </button>
          ))}
        </div>
        <button onClick={() => setShowUpload(!showUpload)} data-testid="archives-upload-toggle" className="btn-sovereign">
          {showUpload ? <span>Close</span> : <><span>Upload</span> <Upload size={14} strokeWidth={1.5} /></>}
        </button>
      </div>

      {showUpload && (
        <form onSubmit={upload} className="sov-border bg-[#0A0A0A] p-8 mb-8 fade-up" data-testid="archives-upload-form">
          <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] mb-6">Seal New Document</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Title</label>
              <input data-testid="archive-title" className="input-sovereign" value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} required />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Category</label>
              <select data-testid="archive-category" className="input-sovereign cursor-pointer" value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                {CATEGORIES.filter((c) => isAdmin || c === 'Investor KYC').map((c) => (
                  <option key={c}>{c}{TRIGGER_CATEGORIES.includes(c) ? ' ⚡' : ''}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">File (max 12MB)</label>
              <input data-testid="archive-file" type="file" onChange={onFile} required
                className="text-sm text-[#F5F5F5] file:mr-4 file:py-2 file:px-4 file:border file:border-[#D4AF37] file:bg-transparent file:text-[#D4AF37] file:text-[10px] file:tracking-[0.3em] file:uppercase file:cursor-pointer hover:file:bg-[#D4AF37] hover:file:text-[#050505]" />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] block mb-2">Description (optional)</label>
              <textarea rows={2} className="input-sovereign resize-none" value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </div>
          </div>
          {TRIGGER_CATEGORIES.includes(form.category) && (
            <div className="mb-6 p-4 border border-[#D4AF37]/40 bg-[#D4AF37]/5">
              <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">
                <Crown size={12} strokeWidth={1.5} />
                {form.category === 'Marriage Certificate' ? 'Tier 2 Trigger' : 'Tier 3 Trigger'} — verification will flip the doctrine permanently.
              </div>
            </div>
          )}
          <button type="submit" disabled={uploading} data-testid="archive-submit" className="btn-sovereign">
            {uploading ? <><Loader2 size={14} className="animate-spin" /> <span>Sealing</span></> :
              <><span>Seal in Archives</span> <Upload size={14} strokeWidth={1.5} /></>}
          </button>
        </form>
      )}

      {filtered.length === 0 ? (
        <div className="sov-border bg-[#0A0A0A] p-16 text-center">
          <FileText size={28} className="text-[#D4AF37] mx-auto mb-4" strokeWidth={1} />
          <p className="text-sm text-[#A3A3A3] font-light">No documents in this lane.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((d) => (
            <div key={d.id} data-testid={`archive-doc-${d.id}`} className="sov-border bg-[#0A0A0A] p-5 flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2 flex-wrap">
                  <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] border border-[#D4AF37]/40 px-2 py-1">{d.category}</span>
                  {TRIGGER_CATEGORIES.includes(d.category) && (
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] flex items-center gap-1">
                      <Crown size={10} strokeWidth={1.5} /> Trigger
                    </span>
                  )}
                  {d.verified && (
                    <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] flex items-center gap-1">
                      <Shield size={10} strokeWidth={1.5} /> Verified
                    </span>
                  )}
                </div>
                <div className="text-sm font-light tracking-wide truncate">{d.title}</div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mt-1 truncate">
                  {d.filename} · {(d.size_bytes / 1024).toFixed(1)} KB · sha {d.sha256.slice(0, 12)}…
                </div>
              </div>
              <div className="flex items-center gap-2">
                <ActionBtn icon={Download} onClick={() => download(d.id)} title="Download" />
                {isAdmin && !d.verified && (
                  <ActionBtn icon={Check} onClick={() => verify(d.id)} title="Verify" disabled={busy === d.id} highlight />
                )}
                <ActionBtn icon={Trash2} onClick={() => remove(d.id)} title="Delete" disabled={busy === d.id} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const LineageIndicator = ({ lineage }) => (
  <div data-testid="lineage-indicator" className="sov-border bg-[#0A0A0A] p-6 md:p-8 mb-12">
    <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
      <div className="text-[10px] tracking-[0.4em] uppercase text-[#D4AF37] flex items-center gap-3">
        <Crown size={12} strokeWidth={1.5} /> Lineage Status · {lineage.current_tier_name}
      </div>
      <div className="font-display text-3xl text-[#D4AF37]">{lineage.doctrine_active}</div>
    </div>
    <div className="grid grid-cols-3 gap-3 mb-4">
      {lineage.tiers.map((t) => (
        <div
          key={t.tier}
          className={`p-4 border transition-colors ${
            t.active ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#2a2a2a] opacity-50'
          }`}
        >
          <div className="text-[9px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-1">Tier {t.tier} · {t.name}</div>
          <div className={`font-display text-2xl ${t.active ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'}`}>{t.split}</div>
          {t.trigger && (
            <div className="text-[9px] tracking-[0.2em] uppercase text-[#A3A3A3] mt-2 truncate">↑ {t.trigger}</div>
          )}
          {t.active && t.fired_at && (
            <div className="text-[9px] tracking-[0.2em] uppercase text-[#D4AF37] mt-1">
              Fired {new Date(t.fired_at).toLocaleDateString()}
            </div>
          )}
        </div>
      ))}
    </div>
    <div className="text-[11px] tracking-[0.15em] uppercase text-[#A3A3A3] italic font-light">
      {lineage.path_to_ancestral}
    </div>
  </div>
);

const Stat = ({ label, value, accent }) => (
  <div>
    <div className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3] mb-2">{label}</div>
    <div className={`metric-number text-3xl md:text-4xl ${accent ? 'text-[#D4AF37]' : ''}`}>{value}</div>
  </div>
);

const ActionBtn = ({ icon: Icon, onClick, title, disabled, highlight }) => (
  <button onClick={onClick} title={title} disabled={disabled}
    className={`p-2 border transition-colors disabled:opacity-30 ${
      highlight ? 'border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#050505]'
                : 'border-[#444] text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37]'
    }`}>
    <Icon size={12} strokeWidth={1.5} />
  </button>
);
